/**
 * The "Forecast input" card of the cycle rail (f11.3), in the prototype's
 * `.step-rail` idiom from `cycle-workflow.html`.
 *
 * One link per Business Unit the SERVER says the caller has lines in, to the
 * f11.1 grid. Every condition reads the entry -- `shown`, `mode` and the
 * per-unit counts -- so the client never decides what is visible or editable.
 * This replaces the legacy per-unit `SENT` workbook distribution.
 */

import { Link, useSearchParams } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';

import type { CycleInputEntry, CycleInputEntryUnit } from '@/api/client';

import { inputEntryFoot, unitMeta } from './inputEntryView';

export function ForecastInputCard({ input }: { input: CycleInputEntry }) {
  const [params] = useSearchParams();
  const { entry, cycle } = input;
  if (!entry.shown || !entry.mode) return null;

  const openUnitId = params.get('bu');
  const count = entry.business_units.length;

  return (
    <div className="card cycle-rail-card forecast-input-card" data-testid="forecast-input-card">
      <div className="card-head">
        <h3>Forecast input</h3>
        <span className="hint">
          {count} {count === 1 ? 'unit' : 'units'}
        </span>
      </div>
      <div className="card-body cycle-rail-body">
        <nav className="step-rail cycle-rail" aria-label="Forecast input by Business Unit">
          {entry.business_units.map((unit: CycleInputEntryUnit) => {
            const current = unit.id === openUnitId;
            const editable = entry.mode === 'INPUT' && unit.editable_line_count > 0;
            return (
              <Link
                key={unit.id}
                to={`/cycles/${encodeURIComponent(cycle.id)}/forecast?bu=${encodeURIComponent(unit.id)}`}
                className={`step ${current ? 'is-current' : editable ? 'is-input' : 'is-todo'}`}
                aria-current={current ? 'page' : undefined}
                data-unit={unit.id}
              >
                <span className="step-num" aria-hidden="true">
                  <LayoutGrid />
                </span>
                <span className="step-body">
                  <span className="step-title">{unit.name}</span>
                  <span className="step-meta">{unitMeta(unit, entry.mode)}</span>
                </span>
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="card-foot">
        <span className={`hint ${entry.mode === 'INPUT' ? 'forecast-input-open' : ''}`}>
          {inputEntryFoot(input)}
        </span>
      </div>
    </div>
  );
}
